import { produce } from "immer";
import { type InjectedIntl } from "react-intl";

import { logger } from "@jslib/shared/utils/logger";

import { EventName, recordGaEvent } from "@/lib/GoogleAnalytics/ga";

import {
  type FieldId,
  type FieldValidationErrors,
  type Organization,
  type VerificationService,
  type FieldContent,
  type FieldRef,
  type ExtendedFieldId,
  type WithOrganization,
  type InputSelectOnKeyDown,
  type FormSelectChoice,
  type Country,
  type ProgramTheme,
  type Locale,
  type ViewModel,
  type State,
  type PendingResponse,
  type DocUploadResponse,
  type FieldDescription,
  type AllStatuses,
  type PersonalInfoFieldId,
  type Options,
} from "../../types/types";
import { type VerificationStepsEnum } from "../../types/runtimeTypes";
import { blankOrganization } from "../../types/empties";
import {
  validateFieldById,
  getFieldValidationErrors,
  isFormErrored,
} from "../../validators/validators";
import { setFocus } from "../browser/inputHelpers";
import { getRefByFieldId, getRefs } from "../../refs/refs";
import { getSafe } from "../getSafe";
import {
  getConfiguredCountries,
  getAvailableLocales,
  getConfiguredStates,
  getEstimatedReviewTime,
  getMaxReviewTime,
} from "../../ProgramTheme/programThemeGetters";
import { getQueryParamsFromUrl } from "../routing/Url";
import { FIELD_IDS } from "../../../constants";
import { getOptions } from "../../../options/options";

export const updateViewModelOrganization = (
  organization: Organization,
  verificationService: VerificationService,
) => {
  const viewModel = verificationService.viewModel as WithOrganization;
  const nextState = produce(viewModel, (draft) => {
    draft.organization = organization
      ? {
          id: organization.id,
          idExtended: organization.idExtended,
          name: organization.name,
          city: organization.city,
          state: organization.state,
          source: organization.source,
        }
      : blankOrganization.organization;
    if (viewModel.organization && viewModel.organization.source !== undefined) {
      draft.organization.source = viewModel.organization.source;
    }
  });
  verificationService.updateViewModel(nextState as ViewModel);
};

export const populateViewModelFromQueryParams = (verificationService: VerificationService) => {
  const viewModel = verificationService.viewModel as WithOrganization;
  const queryParams = getQueryParamsFromUrl();

  const nextState = produce(viewModel, (draft) => {
    Object.keys(viewModel).forEach((key) => {
      if (queryParams.has(key)) {
        draft[key] = queryParams.get(key);
      }
    });
  });

  verificationService.updateViewModel(nextState as ViewModel);
};

export const updateFieldValidationErrors = (
  fieldValidationErrors: FieldValidationErrors,
  verificationService: VerificationService,
) => {
  verificationService.updateFieldValidationErrors(fieldValidationErrors);
};

export const updateFieldValidationErrorsByFieldId = (
  fieldId: FieldId | ExtendedFieldId,
  value: FieldContent,
  verificationService: VerificationService,
) => {
  const { fieldValidationErrors } = verificationService;
  const nextState = produce(fieldValidationErrors, (draft) => {
    if (draft[fieldId]) {
      draft[fieldId] = validateFieldById(fieldId, value, verificationService.formValidationOptions);
    }
  });
  updateFieldValidationErrors(nextState, verificationService);
};

export const shouldCollectAddressFields = (
  countryChoice: FormSelectChoice<Country, string>,
  countries: Country[],
): boolean =>
  (getSafe(() => countries.length === 1) && getSafe(() => countries[0]) === "US") ||
  getSafe(() => countryChoice.value === "US") ||
  getSafe(() => countryChoice.value === undefined);

export const getStatusLabel = (intl: InjectedIntl, status: AllStatuses | undefined): string => {
  if (!status) {
    return "";
  }
  return intl.formatHTMLMessage({
    id: status,
    defaultMessage: status,
  });
};

export const getStatusOptions = (
  intl: InjectedIntl,
  statuses: AllStatuses[] | undefined,
): FormSelectChoice[] => {
  if (!statuses) {
    return [];
  }
  return statuses.map((status) => ({
    value: status,
    label: getStatusLabel(intl, status),
  }));
};

export const orgToOption = (org: Organization) => ({
  value: org.id,
  label: org.name,
  country: org.country,
});

/**
 * @private
 */
export const getFieldDisplayOrderFromRefs = (): FieldId[] | ExtendedFieldId[] => {
  const refs: FieldRef[] = getRefs();
  const fieldDisplayOrder: FieldId | ExtendedFieldId[] = [];

  refs.map((refObject: FieldRef) => fieldDisplayOrder.push(refObject.fieldId));

  return fieldDisplayOrder;
};

/**
 * HD-638 - focus on year, rather than month (which is an open <select> that covers error msg)
 */
export const adjustFirstErroredFieldId = (
  firstErroredFieldId: FieldId | ExtendedFieldId,
): FieldId | ExtendedFieldId => {
  if (firstErroredFieldId === "birthDate" || firstErroredFieldId === "birthDateDay") {
    return "birthDateYear";
  }
  return firstErroredFieldId;
};

/**
 * @private
 */
export const getFirstErroredFieldId = (
  fieldDisplayOrder: FieldId[] | ExtendedFieldId[],
  fieldValidationErrors: FieldValidationErrors,
): FieldId | ExtendedFieldId => {
  let firstErroredFieldId: FieldId | ExtendedFieldId;

  fieldDisplayOrder.reverse().forEach((field) => {
    if (fieldValidationErrors[field]) {
      firstErroredFieldId = field;
    }
  });

  return adjustFirstErroredFieldId(firstErroredFieldId);
};

/**
 * @private
 */
export const handleEmailOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  const submitButtonRef: HTMLElement = getRefByFieldId("submitButton");
  if (event.key === "Enter" && submitButtonRef) {
    submitButtonRef.click();
  }
};

/**
 * @deprecated Accessibility standards prevent setting focus without user keyboard interaction
 */
export const handleCountryOnKeyDown: InputSelectOnKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  if (event.key === "Tab" && !event.shiftKey) {
    event.preventDefault();
    setFocus("organization");
  }
};

/**
 * @public
 */
export const submitForm = (
  viewModel: ViewModel,
  verificationService: VerificationService,
  step: VerificationStepsEnum,
  successCallback?: () => void,
) => {
  const currentFieldValidationErrors = getFieldValidationErrors(
    viewModel,
    verificationService.formValidationOptions,
  );
  const isFormValid = !isFormErrored(currentFieldValidationErrors);

  if (isFormValid) {
    logger.info(`${step} submitting form`);
    verificationService.submitStep(step, viewModel, verificationService.verificationResponse);
    if (successCallback) {
      successCallback();
    }
  } else {
    const fieldDisplayOrder: FieldId[] | ExtendedFieldId[] = getFieldDisplayOrderFromRefs();
    const firstErroredFieldId: FieldId | ExtendedFieldId = getFirstErroredFieldId(
      fieldDisplayOrder,
      currentFieldValidationErrors,
    );
    recordGaEvent({
      eventName: EventName.INVALID_FORM_SUBMIT,
      params: {
        fieldValidationErrors: JSON.stringify(currentFieldValidationErrors),
      },
    });
    updateFieldValidationErrors(currentFieldValidationErrors, verificationService);
    setFocus(firstErroredFieldId);
  }
};

/**
 * @public
 * @description Help with commonly-accessed values. Returns the fully-resolved verbiage in the appropriate language.
 * @return {Object}
 */
export const getEstAndMaxReviewTimes = (
  verificationResponse: DocUploadResponse | PendingResponse,
  programTheme: ProgramTheme,
  intl: any,
): { estReviewTime: string; maxReviewTime: string } => {
  const estReviewTime = intl.formatHTMLMessage({
    id: `dateTime.${
      getSafe(() => verificationResponse.estimatedReviewTime) ||
      getEstimatedReviewTime(programTheme)
    }`,
    default: "a few minutes",
  });
  const maxReviewTime = intl.formatHTMLMessage({
    id: `dateTime.${
      getSafe(() => verificationResponse.maxReviewTime) || getMaxReviewTime(programTheme)
    }`,
    default: "2 hours",
  });
  return {
    estReviewTime,
    maxReviewTime,
  };
};

/**
 * @private
 */
export const getAvailableCountryChoices = (
  programTheme: ProgramTheme,
  intl: InjectedIntl,
): FormSelectChoice<Country, string>[] => {
  const availableCountries: Country[] = getConfiguredCountries(programTheme);
  return availableCountries.map((countryCode) => ({
    value: countryCode,
    label: intl.formatHTMLMessage({
      id: `countries.${countryCode}`,
      defaultMessage: countryCode,
    }),
  }));
};

/**
 * @private
 */
export const getSortedCountryChoices = (
  programTheme: ProgramTheme,
  intl: InjectedIntl,
  locale?: Locale,
): FormSelectChoice<Country, string>[] => {
  const localeAwareSorter = (a, b) => Intl.Collator(locale).compare(a.label, b.label);
  return getAvailableCountryChoices(programTheme, intl).sort(localeAwareSorter);
};

/**
 * @private
 */
export const getSortedIdCheckCountryChoices = (
  countries: Country[],
  intl: InjectedIntl,
  locale?: Locale,
): FormSelectChoice<Country, string>[] => {
  const localeAwareSorter = (a, b) => Intl.Collator(locale).compare(a.label, b.label);
  return countries
    .map((countryCode) => ({
      value: countryCode,
      label: intl.formatHTMLMessage({
        id: `countries.${countryCode}`,
        defaultMessage: countryCode,
      }),
    }))
    .sort(localeAwareSorter);
};

export const getAvailableStateChoices = (
  programTheme: ProgramTheme,
  intl: InjectedIntl,
): FormSelectChoice<State, string>[] => {
  const availableStates: State[] = getConfiguredStates(programTheme);
  return availableStates.map((stateCode) => ({
    value: stateCode,
    label: intl.formatHTMLMessage({
      id: `states.${stateCode}`,
      defaultMessage: stateCode,
    }),
  }));
};

/**
 * @deprecated Not used anywhere and performs a noop.
 */
export const produceDraftViewModelWithRequiredFields = <T extends ViewModel>(
  previousModel: T,
  newRequiredFields?: FieldDescription[],
  conditionalRequiredFieldKeys?: (keyof T)[],
) => {
  logger.info(
    "produceDraftViewModelWithRequiredFields received. This method is now deprecated and performs a noop, returning previousViewModel",
    {
      previousModel,
      newRequiredFields,
      conditionalRequiredFieldKeys,
    },
  );

  return previousModel;
};

export const produceDraftViewModelWithAllRequiredFields = <T extends ViewModel>(
  viewModel: T,
  newRequiredFields: FieldDescription[],
) =>
  produce(viewModel, (draft: T) => {
    FIELD_IDS.forEach((requiredFieldKey) => {
      if (newRequiredFields.some((field) => field.key === requiredFieldKey)) {
        (draft[requiredFieldKey] as any) =
          viewModel[requiredFieldKey] !== undefined ? viewModel[requiredFieldKey] : "";
      } else {
        delete draft[requiredFieldKey];
      }
    });
  });

/**
 * @private
 */
export const getAvailableLocaleChoices = (
  programTheme: ProgramTheme,
  intl: InjectedIntl,
): FormSelectChoice<Locale, string>[] => {
  const availableLocales: Locale[] = getAvailableLocales(programTheme);
  return availableLocales.map((locale) => ({
    value: locale,
    label: intl.formatHTMLMessage({
      id: `locales.${locale}`,
      defaultMessage: locale,
    }),
  }));
};

/**
 * @private
 */
export const getDefaultCountryChoice = (
  countryChoices: FormSelectChoice<Country, string>[],
): FormSelectChoice<Country, string> => {
  if (countryChoices.length > 0) {
    return countryChoices[0];
  }
  return { value: "US", label: "United States" };
};

/**
 * @private
 */
export const produceDraftViewModel = <T extends ViewModel>(
  previousModel: T,
  key: keyof T,
  value: any,
) =>
  produce(previousModel, (draft: T) => {
    (draft[key] as any) = value;
  });

/**
 * @private
 */
export const getRememberMeRedirectDestination = (
  locale: Locale,
  programId: string,
  verificationId: string,
): string => {
  const supportBasePreviewUrl = "https://support-preview.sheerid.net";
  const supportBaseProdUrl = "https://support.sheerid.com";
  let baseUrl = supportBaseProdUrl;
  const { host } = new URL(window.location.href);

  if (host.includes("preview") || host.includes("localhost")) {
    baseUrl = supportBasePreviewUrl;
  }

  return `${baseUrl}/${locale}/${programId}/${verificationId}/remember-me/`;
};

export const getFieldConfigByFieldId = (
  fieldId: PersonalInfoFieldId,
  options: Options = getOptions(),
) => {
  const fieldConfig = options.formFieldConfig;
  return fieldConfig.find((option) => option.fieldId === fieldId);
};
